import type { ConditionDto } from "../api/types";

/**
 * The condition as the preview endpoints will accept it.
 *
 * A retired leaf is output-only on the wire: the server refuses a body that
 * carries one, so a rule that still holds one cannot be previewed as is. Each
 * such leaf is replaced by an empty `any` — the server's own "matches nothing",
 * and the very answer the engine gives a retired leaf — rather than cut out:
 *
 *  - the matching-transactions preview stays truthful, as a retired leaf
 *    under `all` still voids the group instead of quietly widening it;
 *  - the entity preview keys its answers by each leaf's path in the posted
 *    tree, and a substitution keeps every other leaf where the editor looks
 *    it up.
 *
 * A tree that is nothing but a retired leaf becomes an empty `any` outright:
 * the request is still made and answers with no hits, the same as an empty
 * group the user emptied by hand.
 *
 * A "shared match" whose set is not picked yet is swapped the same way. Its
 * blank id is not a set id at all, so the server refuses the whole body as
 * malformed; until a set is picked it names nothing, which is what the empty
 * `any` says — and the leaves around it keep their live previews.
 */
export const previewableCondition = (condition: ConditionDto): ConditionDto => {
  switch (condition.type) {
    case "retired":
      return { type: "any", conditions: [] };
    case "ref":
      return condition.set ? condition : { type: "any", conditions: [] };
    case "all":
    case "any":
      return {
        ...condition,
        conditions: condition.conditions.map(previewableCondition),
      };
    default:
      return condition;
  }
};
